import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, ArrowLeft, CheckCircle, Clock, CreditCard, Crown, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegularizacaoPage = () => {
  const { user, subscriptionStatus, logout } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API}/payment/plans`);
        setPlans(response.data.plans || []);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleCheckout = (checkoutUrl) => {
    if (!checkoutUrl) {
      window.alert("Link de pagamento não configurado. Entre em contato: suporte@perytus.com.br");
      return;
    }
    const url = new URL(checkoutUrl);
    if (user?.email) {
      url.searchParams.set("email", user.email);
    }
    window.open(url.toString(), "_blank");
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleDateString("pt-BR") : "-");

  return (
    <div className="min-h-screen bg-[#F5F2EB] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#718096] hover:text-[#0F4C5C] mb-4">
          <ArrowLeft size={18} /> Voltar ao painel
        </button>
        <h1 className="font-serif text-3xl font-medium text-[#1A202C]">Regularize sua Assinatura</h1>
        <p className="text-[#718096] mt-2">Escolha o plano ideal para continuar criando seus laudos técnicos</p>

        <div className="bg-white border border-amber-200 rounded-lg p-6 my-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle size={24} className="text-amber-600" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm flex-1">
              <div><span className="text-[#718096]">Usuário:</span><p className="font-medium">{user?.name}</p></div>
              <div><span className="text-[#718096]">Email:</span><p className="font-medium">{user?.email}</p></div>
              <div><span className="text-[#718096]">Plano anterior:</span><p className="font-medium">{subscriptionStatus?.plano || "trial"}</p></div>
              <div><span className="text-[#718096]">Expirou em:</span><p className="font-medium text-red-600">{formatDate(subscriptionStatus?.data_expiracao)}</p></div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C5C]" /></div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {plans.map((plan) => (
              <div key={plan.id} className={`bg-white border rounded-lg overflow-hidden relative ${plan.popular ? "border-[#0F4C5C] ring-2 ring-[#0F4C5C]/20" : "border-[#E2E8F0]"}`}>
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-[#0F4C5C] text-white px-3 py-1 text-xs font-medium rounded-bl-lg flex items-center gap-1">
                    <Crown size={12} /> Mais popular
                  </div>
                )}
                <div className="p-6">
                  <h3 className="font-serif text-xl font-medium text-[#1A202C] mb-1">{plan.name}</h3>
                  <p className="text-[#718096] text-sm mb-4">{plan.billing_period}</p>
                  <div className="mb-6"><span className="text-4xl font-bold text-[#1A202C]">{plan.price_formatted}</span></div>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-[#4A5568]">
                        <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button onClick={() => handleCheckout(plan.checkout_url)} className={`w-full py-6 ${plan.popular ? "" : "bg-white border-2 border-[#0F4C5C] text-[#0F4C5C] hover:bg-[#0F4C5C]/5"}`}>
                    <CreditCard size={18} className="mr-2" /> {plan.popular ? "Assinar Agora" : "Escolher Plano"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 mb-8">
          <h3 className="font-medium text-[#1A202C] mb-4">Enquanto isso, você ainda pode:</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 bg-[#F5F2EB] rounded-lg"><CheckCircle size={20} className="text-green-500" /><span className="text-sm">Visualizar projetos existentes</span></div>
            <div className="flex items-center gap-3 p-3 bg-[#F5F2EB] rounded-lg"><CheckCircle size={20} className="text-green-500" /><span className="text-sm">Exportar laudos já criados</span></div>
            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg"><Clock size={20} className="text-amber-500" /><span className="text-sm">Criar novos projetos (após regularização)</span></div>
          </div>
        </div>

        <div className="text-center text-sm text-[#718096]">
          <p>Após o pagamento, seu acesso será restaurado automaticamente em alguns minutos.</p>
          <p className="mt-2">Dúvidas? Entre em contato: <a href="mailto:suporte@perytus.com.br" className="text-[#0F4C5C] hover:underline">suporte@perytus.com.br</a></p>
        </div>

        <div className="text-center mt-8">
          <button onClick={logout} className="text-sm text-[#718096] hover:text-[#0F4C5C] hover:underline">Sair da conta</button>
        </div>
      </div>
    </div>
  );
};

export default RegularizacaoPage;
